import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material'
import { Cancel } from '@mui/icons-material'
import ReactJson from 'react-json-view'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { selectGame } from 'store/game/slice'
import { selectLocale } from 'store/locale/slice'
import { localizedDateTime } from 'utils/formatDate'

interface Props {
  open: boolean
  close: () => void
}

export default function GameLogsModal({ open, close }: Props): JSX.Element {
  const intl = useIntl()
  const locale = useSelector(selectLocale)
  const { logsStatus, gameLogs } = useSelector(selectGame)

  const getLogName = (log: LogsResult) => {
    const type = log.type
    const date = new Date(log.timestamp)
    const datetime = localizedDateTime(date, locale.language.code)
    return `${type} - ${datetime}`
  }

  return (
    <StyledDialog open={open} onClose={close}>
      <StyledDialogTitle id="form-dialog-title">
        {intl.formatMessage({ id: 'strings.gameModificationLog' })}
        <CloseModal onClick={close} />
      </StyledDialogTitle>
      <DialogContent>
        {logsStatus === 'succeeded' && gameLogs?.length === 0 && (
          <NoLogsMessage>{intl.formatMessage({ id: 'strings.noLogsAvailableForGame' })}</NoLogsMessage>
        )}
        {gameLogs && gameLogs?.length > 0 && (
          <LogsContainer>
            {gameLogs.map((log: LogsResult, i: number) => (
              <ReactJson
                key={i}
                src={log}
                name={getLogName(log)}
                displayDataTypes={false}
                displayObjectSize={false}
                collapsed={i === 0 ? 1 : true}
                enableClipboard={false}
              />
            ))}
          </LogsContainer>
        )}
        {logsStatus === 'loading' && (
          <SpinnerContainer>
            <LoadingSpinnerGreen size={36} />
          </SpinnerContainer>
        )}
      </DialogContent>
      <StyledDialogActions>
        <CloseButton onClick={close}>{intl.formatMessage({ id: 'strings.close' })}</CloseButton>
      </StyledDialogActions>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    min-width: 90%;
    height: 90%;
  }
`

const StyledDialogTitle = styled(DialogTitle)`
  background-color: #404040;
  color: ${(props) => props.theme.customPalette.light};
`

const CloseModal = styled(Cancel)`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
`

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

const LogsContainer = styled.div`
  display: block;
  margin: auto;
  width: 95%;
  min-height: 95%;
  border: 1px solid #d3d3d3;
  border-radius: 3px;
  padding: 15px;
`

const StyledDialogActions = styled(DialogActions)`
  width: 90%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: auto;
`

const CloseButton = styled(Button)`
  background-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.customPalette.light};
  width: 220px;
  height: 50px;
  border-radius: 6px;

  &:hover {
    background-color: ${(props) => props.theme.palette.primary.main};
    opacity: 0.85;
  }
`

const NoLogsMessage = styled.div`
  display: flex;
  margin: 50px auto;
  width: 90%;
`
